// The Recordings tab (AdminRecordings.dc.html): every recording the hub holds, grouped by product (the
// critic's missed 9: The Funding Engine's recordings are the product's, not 1 intake's), with each
// product's sessions still waiting for a recording, then a group per open 1-2-1 space; and the editor
// for the one picked, with the gold bar on it in the list.
//
// Adam, 13 Oct ("you have to scroll all the way down ... then all the way back up to add a thumbnail,
// and then down to click Save"): the list scrolls in its own box, beside the editor on a laptop and
// above it where they stack (980px and under), with each product's heading pinned at its top. A heading
// folds its product away, remembered in this browser; the product holding the item opened from the
// address or Tonight's shows open for that visit. Picking a row leaves the page where it is when the
// editor's top is on screen and brings it into view when it isn't (always when stacked), puts the
// item in the address, and moves focus to the editor's heading when stacked or picked from the
// keyboard. With anything not saved, picking another row asks first; a row picked while a save is still
// going opens once it's saved (and not at all if it fails, so the problem line stays). The item that
// opens first is fixed when the tab opens, so folding a product never swaps what's in the editor. Each
// row of a recording with its own thumbnail carries a small mark. The editor's foot is pinned to the
// bottom of the screen (RecordingEditor.jsx, ClientEditor.jsx).
// A product made in Add a product (the product-types plan, 13 Oct): a masterclass's video still to add and a
// new on-demand course's modules with no recording yet are rows of their own, opening the same editor, and
// such a course's group ends with Add module (owner and admin).
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useLocation } from '../../lib/router.jsx'
import { useStacked } from '../../lib/media.js'
import { Button, Dialog, Dot, EmptyState } from '../ui/index.js'
import { Icon } from '../shell/Icons.jsx'
import { COPY } from './copy.js'
import { StateChip } from './Head.jsx'
import { addressFor, askedItem, buildGroups, headingFor, pickItem, takesThumbnail } from './model.js'
import { addModule } from './data.js'
import { RecordingEditor } from './RecordingEditor.jsx'
import { ClientEditor } from './ClientEditor.jsx'

// The products folded away in the list, kept in this browser only. Storage that can't be read or
// written (a private window, blocked site data) leaves everything open.
const FOLDS = 'hub-admin-recordings-folded'
function storedFolds() {
  try {
    const keys = JSON.parse(window.localStorage.getItem(FOLDS) || '[]')
    return new Set(Array.isArray(keys) ? keys.filter(k => typeof k === 'string') : [])
  } catch {
    return new Set()
  }
}
function storeFolds(keys) {
  try { window.localStorage.setItem(FOLDS, JSON.stringify([...keys])) } catch { /* kept for this visit only */ }
}

// the address follows the pick, as Webinars' does, so a reload or Back opens the same item
const follow = to => { try { window.history.replaceState({}, '', to) } catch { /* not a browser history */ } }

export function RecordingsTab({ data, reload, api, role, personId }) {
  const nowMs = Date.now()
  const { query } = useLocation()
  const asked = { recording: query.get('recording'), session: query.get('session'), clientSession: query.get('client_session'), item: query.get('item') }
  const groups = buildGroups(data, nowMs, { keepSession: asked.session })
  const items = groups.flatMap(g => g.items)
  const [folded, setFolded] = useState(storedFolds)
  // the product holding the item opened from the address or Tonight's, open for this visit whatever's stored
  const [shownOpen, setShownOpen] = useState(() => groups.find(g => g.items.includes(askedItem(groups, asked)))?.key ?? null)
  // what's open, fixed from the first draw: a fold, or the clock moving on, never picks another item
  const [selKey, setSelKey] = useState(() => pickItem(groups, asked, { folded, nowMs })?.key ?? null)
  const [version, setVersion] = useState(0)
  const [flash, setFlash] = useState(null)
  // a pick held while the open item has changes not saved: { it, keyboard, from }
  const [held, setHeld] = useState(null)
  const [picks, setPicks] = useState(0)
  const editor = useRef(null)
  const nav = useRef(null)
  const dirty = useRef(false)
  // a save going on in the editor, and a row picked meanwhile ({ it, keyboard }), opened once it's saved
  const saving = useRef(false)
  const waiting = useRef(null)
  // still on this tab: a save that lands after Adam has left changes nothing, the address least of all
  const alive = useRef(true)
  const afterPick = useRef(null)
  const returnTo = useRef(null)
  const stacked = useStacked()
  const ids = useId()
  const onDirty = useCallback(v => { dirty.current = v }, [])
  // a save that ends without landing (a problem, or a length still needed) drops the row picked meanwhile
  const onSaving = useCallback(v => { saving.current = v; if (!v) waiting.current = null }, [])
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  const isOpen = g => !folded.has(g.key) || g.key === shownOpen
  const item = (selKey && items.find(i => i.key === selKey)) || pickItem(groups, asked, { folded, nowMs })

  function toggle(g) {
    const fold = isOpen(g)
    const next = new Set(folded)
    if (fold) next.add(g.key)
    else next.delete(g.key)
    setFolded(next)
    if (g.key === shownOpen) setShownOpen(null)
    // what's stored now, which another tab may have changed, with only this product changed
    const kept = storedFolds()
    if (fold) kept.add(g.key)
    else kept.delete(g.key)
    storeFolds(kept)
  }

  function go(it, keyboard) {
    setSelKey(it.key)
    setFlash(null)
    follow(addressFor(it))
    afterPick.current = { focus: stacked || keyboard }
    setPicks(n => n + 1)
  }

  // a click from the keyboard (Enter or Space) has no mouse press: detail is 0
  function pick(it, e) {
    const keyboard = e?.detail === 0
    if (it.key !== item?.key && saving.current) {
      waiting.current = { it, keyboard }
      return
    }
    if (it.key !== item?.key && dirty.current) {
      setHeld({ it, keyboard, from: e?.currentTarget || null })
      return
    }
    go(it, keyboard)
  }

  function keepEditing() {
    returnTo.current = held?.from || null
    setHeld(null)
  }

  function openWithoutSaving() {
    const { it, keyboard } = held
    dirty.current = false
    setHeld(null)
    go(it, keyboard)
  }

  // After a pick: the editor's top into view only where it's off screen or stacked under the list, and
  // focus on its heading when stacked or picked from the keyboard (a mouse pick on a laptop keeps focus
  // in the list, so Adam keeps his place)
  useEffect(() => {
    const after = afterPick.current
    afterPick.current = null
    const wrap = editor.current
    if (!after || !wrap) return
    const top = wrap.getBoundingClientRect().top
    if (stacked || top < 0 || top > window.innerHeight - 120) wrap.scrollIntoView({ block: 'start' })
    if (after.focus) wrap.querySelector('.adc-h2')?.focus({ preventScroll: true })
  }, [picks]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep editing: focus back on the row that asked
  useEffect(() => {
    if (held || !returnTo.current) return
    const row = returnTo.current
    returnTo.current = null
    if (row.isConnected) row.focus()
  }, [held])

  // The open row in view inside the list, on opening, from the address and after a save, by scrolling
  // the list itself and never the page
  useEffect(() => {
    const box = nav.current
    if (!box || box.scrollHeight <= box.clientHeight) return
    const row = box.querySelector('.adc-item[aria-current="true"]')
    if (!row || !row.offsetParent) return
    const head = row.closest('.adc-group')?.querySelector('.adc-group-head')?.offsetHeight || 0
    if (row.offsetTop - head < box.scrollTop || row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = Math.max(0, row.offsetTop - head - 8)
    }
  }, [item?.key])

  // After a save: the rows again, the same item picked (a session's new recording is now its item, and
  // the address follows it), and the line that says what happened; or, with a row picked while it saved,
  // that row. Nothing at all once the tab has gone.
  const saved = prefix => async (id, line) => {
    await reload()
    if (!alive.current) return
    // saved, so nothing is left unsaved while the editor draws again
    dirty.current = false
    saving.current = false
    const next = waiting.current
    waiting.current = null
    if (next) return go(next.it, next.keyboard)
    const key = `${prefix}:${id}`
    setSelKey(key)
    setFlash({ key, text: line })
    setVersion(v => v + 1)
    follow(prefix === 'c' ? `/admin/recordings?client_session=${id}` : `/admin/recordings?recording=${id}`)
  }

  if (!items.length && !groups.some(g => g.addModule)) return <EmptyState>{COPY.noRecordings}</EmptyState>

  const shared = {
    api, role, personId, onDirty, onSaving, flash: flash && flash.key === item.key ? flash.text : null, clearFlash: () => setFlash(null),
  }
  return (
    <div className="adc-rec">
      <nav className="adc-list" aria-label={COPY.listLabel} ref={nav}>
        {groups.map((g, n) => {
          const open = isOpen(g)
          return (
            <div key={g.key} className="adc-group">
              <button type="button" className="adc-group-head adc-group-fold" aria-expanded={open} aria-controls={`${ids}-${n}`} onClick={() => toggle(g)}>
                <Dot product={g.client ? null : g.product} colour={g.client ? 'var(--shell-ink)' : undefined} />
                <span className="adc-group-name">{g.name}</span>
                <Icon name="chevron" size={16} className="ad-icon adc-group-chev" />
              </button>
              <div id={`${ids}-${n}`} role="group" aria-label={g.name} hidden={!open}>
                {g.items.map(it => (
                  <button key={it.key} type="button" className="adc-item" aria-current={it.key === item?.key ? 'true' : undefined} onClick={e => pick(it, e)}>
                    <span className="adc-item-text">
                      <span className="adc-item-name">{it.name}</span>
                      {it.sub && <span className="adc-item-sub">{it.sub}</span>}
                    </span>
                    {takesThumbnail(it) && it.recording?.thumbnail_path && (
                      <span className="adc-item-pic" role="img" aria-label={COPY.hasThumbnail} title={COPY.hasThumbnail}>
                        <Icon name="picture" size={16} />
                      </span>
                    )}
                    <StateChip chip={it.chip} />
                  </button>
                ))}
                {g.addModule && (role === 'owner' || role === 'admin') && (
                  <AddModule product={g.product} label={g.addModule.label} data={data} api={api} reload={reload} />
                )}
              </div>
            </div>
          )
        })}
      </nav>
      <div className="adc-editor-wrap" ref={editor}>
        {item && item.kind === 'client' && (
          <ClientEditor key={`${item.key}:${version}`} item={item} onSaved={saved('c')} {...shared} />
        )}
        {item && item.kind !== 'client' && (
          <RecordingEditor key={`${item.key}:${version}`} item={item} data={data} onSaved={saved('r')} {...shared} />
        )}
      </div>
      {held && (
        <Dialog title={COPY.unsavedTitle} onClose={keepEditing} footer={(
          <>
            <Button onClick={openWithoutSaving}>{COPY.openWithoutSaving}</Button>
            <Button kind="ink" onClick={keepEditing}>{COPY.keepEditing}</Button>
          </>
        )}>
          <p className="ad-lede">{COPY.unsavedBody(headingFor(item), headingFor(held.it))}</p>
        </Dialog>
      )}
    </div>
  )
}

// A new on-demand course's next module, at the foot of its group: added, then the list again with its row
function AddModule({ product, label, data, api, reload }) {
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  async function add() {
    if (busy) return
    setBusy(true)
    setLine(null)
    try {
      const made = await addModule(api, { product, data })
      await reload()
      setLine({ text: COPY.moduleAdded(made?.label || label.replace(/^Add /, '')) })
    } catch (e) {
      setLine({ bad: true, text: api.words(e) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="adc-add-module">
      <Button size="sm" onClick={add} busy={busy}>{label}</Button>
      {line && <span className={line.bad ? 'ad-error' : 'adc-flash'} role={line.bad ? 'alert' : 'status'}>{line.text}</span>}
    </div>
  )
}
